import {
  FileDownloadOutlined,
  CancelOutlined,
  RestoreOutlined,
  AccountBalanceOutlined,
  MoreHorizOutlined,
} from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BankReconciliationTab } from 'adminConstants/routes';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';
import TabsPanel from 'further-ui/components/TabsPanel';

import BankReconciliationContextProvider, {
  useBankReconciliationContext,
} from 'contexts/BankReconciliationContext';
import useManageBankAccount from 'hooks/lp/data/bankAccount/useManageBankAccount';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import ErrorBoundary from 'components/ErrorBoundary';
import Button from 'components/Button';

import Page from 'components/new/Page';
import BankAccountSelect from 'components/new/BankAccountSelect';
import { useDrawerUrlState } from 'hooks/ui/useDrawerUrlState';

import AddNewBankAccount from './AddNewBankAccount';
import ImportStatement from './ImportStatement';
import ProcessBankAccount from './ProcessBankAccount';
import BankAccountBalances from './BankAccountBalances';
import ImportingTransactions from './ImportingTransactions';
import BankStatement from './BankStatement';
import AccountSettings from './AccountSettings';
import ReconcileTransactions from './ReconcileTransactions';
import ReconciledTransactions from './ReconciledTransactions';
import Menu from 'further-ui/components/Menu';
import NoAccounts from './NoAccounts';
import ProcessingPage from 'components/new/ProcessingPage';

const RenewButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  borderColor: theme.palette.common.white,
  fontWeight: theme.typography.fontWeightBold,

  '&:hover': {
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
  },
}));

const BankReconciliationWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  const [bankAccounts] = api.bankAccount.listBankAccounts.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  return (
    <BankReconciliationContextProvider
      vehicleId={selectedVehicleId ?? ''}
      bankAccounts={bankAccounts ?? []}
    >
      <BankReconciliation />
    </BankReconciliationContextProvider>
  );
};

const BankReconciliation: React.FC = () => {
  const { hash } = useLocation();
  const { selectedBankAccount, setSelectedBankAccountId, bankAccounts } =
    useBankReconciliationContext();
  const validTabs = new Set<string>(Object.values(BankReconciliationTab));
  const defaultTab = validTabs.has(hash)
    ? (hash as BankReconciliationTab)
    : BankReconciliationTab.ReconcileTransactions;
  const {
    refreshBankFeed,
    deactivateBankFeed,
    renewBankConnection,
    isRefreshingBankFeed,
    isPending,
  } = useManageBankAccount(selectedBankAccount);
  const [canCreateBankAccount, canImportOperations, canEditBankAccount] =
    useHasPermissions([
      RolePermission.LpCreateBankAccount,
      RolePermission.LpCreateBankAccountOperation,
      RolePermission.LpEditBankAccount,
    ]);

  useEffect(() => {
    if (
      !selectedBankAccount ||
      selectedBankAccount.operationsCount > 0 ||
      isRefreshingBankFeed
    )
      return;

    refreshBankFeed();
  }, [selectedBankAccount?.id]);

  const isBankFeedConnectionExpired = !!(
    selectedBankAccount?.bankFeedAccessValidTo &&
    selectedBankAccount.bankFeedAccessValidTo <= new Date()
  );

  const { activeDrawer, openDrawer, closeDrawer } = useDrawerUrlState({
    addBankAccount: { mode: null },
    processBankAccount: { ref: '', bankAccountId: null },
    importStatementFile: null,
  });

  const [connectingBankAccountId, setConnectingBankAccountId] = useState<
    string | undefined
  >();
  const handleBankAccountAddClick = () => {
    openDrawer({ type: 'addBankAccount' });
  };

  return (
    <>
      <Page
        tabTitle="Bank reconciliation"
        spacing="lg"
        withStickyHeader
        heading="Bank reconciliation"
        headingIcon={AccountBalanceOutlined}
        withHeadingDivider
        headerBreakpoint="sm"
        customActions={
          bankAccounts.length ? (
            <Menu
              label="Bank account actions"
              items={[
                ...(canCreateBankAccount
                  ? [
                      {
                        label: 'Renew bank connection',
                        icon: RestoreOutlined,
                        onClick: renewBankConnection,
                        disabled:
                          isPending || !selectedBankAccount?.bankFeedAccountId,
                      },
                    ]
                  : []),
                ...(canImportOperations
                  ? [
                      {
                        label: 'Import a statement file',
                        icon: FileDownloadOutlined,
                        onClick: () =>
                          openDrawer({ type: 'importStatementFile' }),
                        disabled: isPending,
                      },
                    ]
                  : []),
                ...(canEditBankAccount
                  ? [
                      {
                        label: 'Deactivate bank feed',
                        icon: CancelOutlined,
                        onClick: deactivateBankFeed,
                        disabled:
                          isPending || !selectedBankAccount?.bankFeedAccountId,
                      },
                    ]
                  : []),
              ]}
              handleSize="small"
              handleIcon={MoreHorizOutlined}
            />
          ) : null
        }
        messages={
          isBankFeedConnectionExpired
            ? [
                {
                  key: 'bank-feed-connection-expired',
                  content: (
                    <Row justify="center" centered spacing="md">
                      <Typography
                        variant="body1"
                        color="text.white"
                        fontWeight="bold"
                      >
                        Your bank feed connection has expired and requires
                        renewal
                      </Typography>
                      {canCreateBankAccount && (
                        <RenewButton
                          variant="outlined"
                          onClick={renewBankConnection}
                          disabled={
                            isPending || !selectedBankAccount?.bankFeedAccountId
                          }
                        >
                          Renew bank feed
                        </RenewButton>
                      )}
                    </Row>
                  ),
                  variant: 'warning',
                },
              ]
            : undefined
        }
      >
        {!bankAccounts.length ? (
          <NoAccounts
            onConnectBankAccount={handleBankAccountAddClick}
            onUploadManually={() => {
              openDrawer({ type: 'addBankAccount', mode: 'manual' });
            }}
          />
        ) : (
          <Column spacing="lg" flex={1}>
            <Row
              justify="space-between"
              fullWidth
              breakpoint="md"
              centered
              spacing="lg"
            >
              <BankAccountSelect
                bankAccounts={bankAccounts ?? []}
                onBankAccountChange={setSelectedBankAccountId}
                onBankAccountAdd={handleBankAccountAddClick}
                selectedBankAccountId={selectedBankAccount?.id}
              />
              <BankAccountBalances
                balanceFromStatement={selectedBankAccount?.balanceFromStatement}
                calculatedBalance={selectedBankAccount?.calculatedBalance}
                accountCurrency={selectedBankAccount?.accountCurrency}
              />
            </Row>

            <TabsPanel
              defaultTab={defaultTab}
              useTabsUrls
              fullHeight
              tabs={[
                {
                  key: BankReconciliationTab.ReconcileTransactions,
                  label: 'Reconcile transactions',
                  urlParam: 'reconcile',
                  content: isRefreshingBankFeed ? (
                    <ImportingTransactions />
                  ) : selectedBankAccount ? (
                    <ErrorBoundary loader={<ProcessingPage fullHeight />}>
                      <ReconcileTransactions
                        bankAccount={selectedBankAccount}
                      />
                    </ErrorBoundary>
                  ) : null,
                },
                {
                  key: BankReconciliationTab.BankStatement,
                  label: 'Bank statement',
                  urlParam: 'statement',
                  content: isRefreshingBankFeed ? (
                    <ImportingTransactions />
                  ) : selectedBankAccount ? (
                    <ErrorBoundary loader={<ProcessingPage fullHeight />}>
                      <BankStatement bankAccount={selectedBankAccount} />
                    </ErrorBoundary>
                  ) : null,
                },
                {
                  key: BankReconciliationTab.ReconciledTransactions,
                  label: 'Reconciled transactions',
                  urlParam: 'reconciled',
                  content: isRefreshingBankFeed ? (
                    <ImportingTransactions />
                  ) : selectedBankAccount ? (
                    <ErrorBoundary loader={<ProcessingPage fullHeight />}>
                      <ReconciledTransactions
                        bankAccount={selectedBankAccount}
                      />
                    </ErrorBoundary>
                  ) : null,
                },
                {
                  key: BankReconciliationTab.AccountSettings,
                  label: 'Account settings',
                  urlParam: 'settings',
                  content: isRefreshingBankFeed ? (
                    <ImportingTransactions />
                  ) : selectedBankAccount ? (
                    <ErrorBoundary loader={<ProcessingPage fullHeight />}>
                      <AccountSettings
                        bankAccount={selectedBankAccount}
                        onConnectBankFeed={(bankAccountId) => {
                          setConnectingBankAccountId(bankAccountId);
                          openDrawer({ type: 'addBankAccount' });
                        }}
                      />
                    </ErrorBoundary>
                  ) : null,
                },
              ]}
            />
          </Column>
        )}
      </Page>
      <AddNewBankAccount
        open={activeDrawer?.type === 'addBankAccount'}
        onClose={() => {
          setConnectingBankAccountId(undefined);
          closeDrawer();
        }}
        bankAccountId={connectingBankAccountId}
        manualEntry={
          activeDrawer?.type === 'addBankAccount' &&
          activeDrawer.mode === 'manual'
        }
      />
      {activeDrawer?.type === 'processBankAccount' && (
        <ProcessBankAccount
          onClose={closeDrawer}
          onRetry={() => {
            setConnectingBankAccountId(
              activeDrawer.bankAccountId ?? connectingBankAccountId,
            );
            openDrawer({ type: 'addBankAccount' });
          }}
        />
      )}
      <ImportStatement
        open={activeDrawer?.type === 'importStatementFile'}
        onClose={closeDrawer}
        bankAccountId={selectedBankAccount?.id ?? ''}
      />
    </>
  );
};

export default BankReconciliationWrapper;
